/*
 * The headless driver — the thing that advances an agent tree when no tab is open.
 *
 * ── The gap it closes ──
 *
 * The lease guarantees exactly ONE driver. It has never guaranteed that there IS one. Close the tab
 * mid-run and the work is not lost — the log and the run records are durable — but nothing advances
 * it until someone re-attaches. That is mutual exclusion without liveness, and it is the difference
 * between "your work survived" and "your work continued".
 *
 * ── Why this is a swap and not a second system ──
 *
 * A headless driver is not a special case; it is a lease holder whose `kind` happens to be SERVER.
 * It claims the same lease, carries the same fencing epoch, calls the same DO methods, and writes
 * the same records as a browser tab. Nothing downstream can tell which one advanced a run, which is
 * exactly the property that makes "browser-driven now, headless later" a deployment decision rather
 * than a rewrite. Every durable-execution engine surveyed reaches liveness by running a worker fleet;
 * this is that, minus the engine, because the state is already written down.
 *
 * ── The boundary: lifecycle here, inference injected ──
 *
 * The driver owns claiming, heartbeating, reconciling, advancing, budget and step enforcement, and
 * release. It does NOT own the model call — that arrives as a `StepFn`. The reason is that the
 * loop's correctness is a property of its lifecycle, not of any model's output, so it must be
 * testable without one.
 *
 * It does NOT currently serve the browser, and saying otherwise would be a claim rather than a fact:
 * this lives in the worker package, which the browser bundle does not import. What it does prove is
 * that the three verbs are sufficient to advance a tree from outside a tab — the browser reaching
 * them over the same HTTP routes is the remaining step, not a second design.
 *
 * ── Takeover is not resumption ──
 *
 * On taking over a tree the driver reconciles first and advances second. A previous driver may have
 * died between dispatching a tool and recording its outcome, so the log can contain calls whose
 * effect on the world is UNKNOWN. On this platform those can be writes to plant. Advancing before
 * reconciling would mean re-deriving from a state nobody has established.
 */
import { create, toBinary, fromBinary } from '@bufbuild/protobuf';
import {
  LeaseRequestSchema,
  LeaseReplySchema,
  LeaseRequest_Action,
  RunMutationSchema,
  RunMutationReplySchema,
  TreeViewRequestSchema,
  TreeViewReplySchema,
  RunProgressSchema,
  AgentReturnSchema,
  RunUsageSchema,
  RunLease_Kind,
  RunStatus,
  TerminalReason,
  type AgentRun,
  type RunUsage,
  type DanglingToolCall,
  type PendingInjection,
} from '@vitesse/proto/vitesse/ai/v1/agent_pb.js';
import type { DriverHost, Credits, CreditReservation } from './host';
import type { StepTelemetry } from './step';

/** One model↔tool round trip for one run. The driver supplies the run and what it has learned; the
 *  implementation returns what to record. Returning `done` ends the run. */
export interface StepResult {
  headline: string;
  usage?: RunUsage;
  /** Tier-1 measurements — wall clock, inference time, TTFT, context composition. Measured on our
   *  side of the wire, so present on every provider and both modalities. See `StepTelemetry`. */
  telemetry?: StepTelemetry;
  /**
   * `unsourced` — figures the answer asserts that appear nowhere in what this run received.
   *
   * Carried on the DONE rather than reported separately, because it describes THIS conclusion and
   * has to travel with it: the return is what a parent reads, and a caveat that arrives by another
   * route is a caveat nobody sees. Empty on the overwhelming majority of runs, which is the point —
   * see `provenance.ts` for why this is evidence and never a verdict.
   */
  done?: {
    terminalReason: TerminalReason;
    summary: string;
    deliverableJson?: string;
    unsourced?: string[];
    /** Claims that CONTRADICT the record — a real reading reported against the wrong subject.
     *  Separate from `unsourced` because they are different findings that call for different
     *  reactions: one says a figure was never measured, the other says it belongs to something
     *  else. They are also mutually exclusive by construction — a swapped value came from
     *  somewhere. */
    misattributed?: string[];
    /** Links the answer cites that appear in no search result the run received. Flat: there is no
     *  honest reading of a source that never came back. */
    uncited?: string[];
    /** Where each figure in the deliverable came from — `4.21 ← readSymbol(PT-1042)`. Not a finding:
     *  present on ordinary answers, so a reader can see a claim and its origin side by side. */
    citations?: string[];
  };
  /** The call succeeded and produced NOTHING usable.
   *
   *  Not the same as unparseable. A model that rambled said something, and reminding it of the format
   *  can work — so that stays progress, and the loop keeps its bias toward over-running rather than
   *  concluding from output nobody could read.
   *
   *  This is the case where that bias inverts: the loop resends an identical request and receives an
   *  identical nothing, paying for each, until the cap. One more call cannot help, so the bias that
   *  costs one extra call in the normal case costs the entire run here. Making it a FIELD rather than
   *  a guess in the loop is what keeps the judgement where the evidence is — the step saw the reply;
   *  the loop only sees a headline. */
  unproductive?: boolean;
  /**
   * Tools this step called that REFUSED, and tools that answered — by name, each at most once.
   *
   * Here for the same reason `unproductive` is: the step saw the outcome and the loop sees only a
   * headline. Without them a run could call one broken tool every turn until its cap, and each of
   * those turns reported "using symbols.search" — indistinguishable from work.
   *
   * Names only, deliberately. A model that cannot get a tool to work rewrites its ARGUMENTS every
   * attempt, so anything keyed on them counts a fresh thing each time and never adds up.
   *
   * `toolsWorked` matters as much as `toolsFailed`: it is what clears the count, so a tool that
   * fails twice and then answers is a tool that was having trouble, not a tool that is broken.
   */
  toolsFailed?: string[];
  toolsWorked?: string[];
  /**
   * What each refusing tool SAID, keyed by name.
   *
   * The names alone tell the loop when to stop; they cannot tell the PARENT why. Its return is one
   * sentence, and pointing it at "the transcript above" pointed it somewhere it cannot read —
   * observed, a parent that could not see the refusal blamed missing RBAC permission and walked the
   * person into editing roles, when a validator regex was at fault.
   */
  toolFailureMessages?: Record<string, string>;
}
export type StepFn = (run: AgentRun, ctx: StepContext) => Promise<StepResult>;

export interface StepContext {
  /** Unresolved tool calls in THIS run's conversation — unknown, not failed. */
  dangling: DanglingToolCall[];
  /** What the user has said to this run since the last step, in the order they said it.
   *
   *  Delivered at a step boundary rather than mid-call, which is the whole distinction between the
   *  three verbs a user actually needs: QUEUE applies after the current unit of work, STEER applies
   *  at the next safe boundary, STOP halts now. Shipping only queue and stop is the documented
   *  failure — users press stop to get steering and lose the work every time. */
  steers: PendingInjection[];
  /**
   * THE FENCE THIS STEP IS RUNNING UNDER.
   *
   * A tool that mutates the run TREE — spawning a child, above all — has to present the epoch of the
   * lease its caller holds, or the insert claims a fresh one and ejects the very run doing the
   * spawning. Only the driver knows that number, so only the driver can supply it, and a step that
   * cannot see it can only offer tools that do not touch the tree.
   *
   * Carried on the context rather than baked into the dispatcher because it changes per takeover:
   * a dispatcher built once per conversation would hold a stale epoch the moment the tree changed
   * hands, which is a fenced write presenting as a tool that mysteriously stopped working.
   */
  epoch: bigint;
  /**
   * THIS IS THE LAST STEP THIS RUN WILL GET.
   *
   * A run used to learn its remaining road only from the continuation message, which is written
   * ONLY when the previous turn was not a tool result — so an agent deep in a chain of tool calls,
   * which is precisely the agent about to run out, was never told. Observed live: an analyst said in
   * prose that it was wrapping up, then kept calling tools until the cap took it, and the parent got
   * "ran out of turns" instead of the answer it was three sentences from writing.
   *
   * Passed down rather than derived in the step builder because the CAP is the driver's business:
   * it holds the run's constraints and its own per-invocation ceiling, and a second computation of
   * "how much road is left" is a second thing to keep in agreement with the loop that actually
   * stops.
   */
  lastCall: boolean;
}

/** How many of one tree's runs a driver steps at once. Enough for a designer's fan-out of widget
 *  authors to actually fan out; few enough that one tree cannot open a dozen model calls. */
export const CONCURRENT_RUNS = 4;
/** How often an idle worker looks for runs that appeared while its siblings were still driving. A
 *  local read of the tree, and a step takes seconds, so this is well below the rate anything new
 *  can arrive and far above what the read costs. */
const REFILL_MS = 500;

/** What a takeover found and did — returned rather than logged, so a caller can surface it. */
export interface TakeoverReport {
  claimed: boolean;
  /** Why not, when the tree is legitimately held by someone else. */
  refusedBecause: string;
  epoch: bigint;
  /** Calls whose effect on the world is UNKNOWN, found before anything was advanced. */
  reconciled: DanglingToolCall[];
  /** Runs this driver carried to a terminal state. */
  advanced: string[];
  /** The takeover stopped early because the tree stopped being this driver's — the user halted
   *  everything, or another driver claimed it. Not an error: the correct response is to re-invoke,
   *  which re-claims and re-reads rather than continuing from a view that is no longer true. */
  fenced: boolean;
}

/** What advancing one run concluded.
 *   advanced — carried to a terminal state.
 *   waiting  — it delegated: some child has not returned, so it yields and is resumed after them.
 *   fenced   — this driver may no longer spend on this TREE; the whole takeover stops.
 *   stale    — this RUN went terminal underneath the driver; move on to the next one. */
/*
 * `parked` is the fifth, and it differs from `waiting` in the one way that matters to the takeover
 * loop: it is NOT progress. A run waiting on its children is picked up again after they move; a run
 * that parked itself — a wait for later, its call deliberately open — has nothing this driver can
 * advance until something outside the tree resolves that call, so counting it as progress would spin
 * the pass forever on a run that will not change.
 */
type DriveOutcome = 'advanced' | 'waiting' | 'parked' | 'fenced' | 'stale';

export interface DriverOptions {
  /** Optional. Defaults to a fresh identifier per instance, because a driver id names a PROCESS and
   *  nothing was generating one — so two sweeper ticks, or two tabs of the same build, shared a
   *  literal string. The store treats a matching id as the same holder, so both were allowed onto a
   *  tree one of them was already mid-step on, and both dispatched the same tool call. Supply one
   *  only when a stable label is wanted; the default is the safe shape. */
  driverId?: string;
  /** Wall-clock ceiling for one takeover, so a runaway tree cannot pin a worker indefinitely. */
  maxStepsPerRun?: number;
  /** Spend ceiling across the whole takeover. Hitting it ends runs at BUDGET, not silently. */
  budgetUsd?: number;
  /** The credit ledger. Absent = unmetered, which is correct for tests and for a deployment that
   *  bills some other way — but a metered deployment MUST supply it, because nothing else in the
   *  loop can stop an unaffordable turn. */
  credits?: Credits;
  /** How long a grant lasts. This is a LIVENESS window, not a work estimate — the heartbeat below is
   *  what keeps it fresh across a long step, so it can stay short and reclaim stays fast. */
  leaseTtlMs?: number;
  /**
   * A RUN JUST REACHED A TERMINAL STATE — told the instant the write lands.
   *
   * Everything watching a tree learns about it by POLLING, every two seconds. That is the right
   * shape for progress, which is continuous and does not matter to the frame; it is the wrong shape
   * for an ENDING, which is a single moment a reader is waiting for. A run that hit its step cap
   * went on looking alive for up to a full poll interval, and the sentence saying so reached the
   * thread a poll after that — so "stop" looked like something that had not happened yet.
   *
   * The driver is the one party that knows at once, because it is the party doing it. This is how
   * it says so. Optional, and failures in it are swallowed by the caller: a watcher's repaint must
   * never be able to interfere with a run's bookkeeping.
   */
  onRunTerminal?: (runId: string) => void;
}

const DEFAULT_MAX_STEPS = 16;

/** How many empty replies in a row end a run. One can be sampling; a second is structural — and the
 *  loop is resending an identical request, so a third would tell us nothing a second did not. */
const UNPRODUCTIVE_LIMIT = 2;
/**
 * How many times ONE tool may refuse a run before the run stops reaching for it.
 *
 * More generous than UNPRODUCTIVE_LIMIT, and for a reason rather than a hedge: a refusal is
 * INFORMATION. It says what was wrong, and a model that reads it frequently fixes the call on the
 * very next turn — a widget author put out of bounds by the checker corrected itself after one.
 * That is the loop working, so ending it at two would cut off the recovery this design wants. An
 * empty reply carries nothing to learn from, which is why that one stops sooner.
 *
 * A third identical refusal is no longer a model learning. It is a tool this run cannot use.
 */
const TOOL_FAILURE_LIMIT = 3;
/** Only a fallback for the window between constructing the driver and holding a lease. The real
 *  number always comes from the claim reply. */
const DEFAULT_LEASE_TTL_MS = 30_000;
/** The FLOOR under every hold, and the first step's estimate before there is any history.
 *
 *  Deliberately small — too high refuses turns an account could afford — but never zero, because a
 *  zero hold is granted forever and is what an under-reporting client would otherwise request. */
const MIN_HOLD_USD = 0.05;

/**
 * A lease-holding, tree-advancing runner with no browser attached.
 *
 * Deliberately stateless between takeovers: everything it needs is in the DO, so two workers racing
 * to adopt the same abandoned tree is resolved by the lease rather than by coordination between them.
 */
export class HeadlessDriver {
  private readonly host: DriverHost;
  private readonly opts: Required<DriverOptions>;
  /** How long the store actually granted, learned from the claim reply. */
  private grantedTtlMs = DEFAULT_LEASE_TTL_MS;

  constructor(host: DriverHost, opts: DriverOptions) {
    this.host = host;
    this.opts = {
      driverId: crypto.randomUUID(),
      maxStepsPerRun: DEFAULT_MAX_STEPS,
      budgetUsd: 0,
      credits: undefined as unknown as Credits, // absent = unmetered
      leaseTtlMs: 0, // 0 → the store's default
      onRunTerminal: () => {}, // nobody watching is the ordinary case (a server sweep)
      ...opts,
    };
  }

  /**
   * Adopt an abandoned tree and drive it to a terminal state.
   *
   * Refusal is a normal outcome, not an error: a live tab holding the lease means the tree is being
   * driven and this worker should leave it alone. The report says so rather than throwing, because
   * "someone else has it" is the expected case in a fleet where several workers sweep for orphans.
   */
  async takeover(rootRunId: string, step: StepFn): Promise<TakeoverReport> {
    const report: TakeoverReport = { claimed: false, refusedBecause: '', epoch: 0n, reconciled: [], advanced: [], fenced: false };

    const claim = fromBinary(
      LeaseReplySchema,
      await this.host.lease(
        toBinary(
          LeaseRequestSchema,
          create(LeaseRequestSchema, {
            action: LeaseRequest_Action.CLAIM,
            rootRunId,
            driverId: this.opts.driverId,
            kind: RunLease_Kind.SERVER,
            ttlMs: this.opts.leaseTtlMs,
          }),
        ),
      ),
    );
    if (claim.result?.error) {
      report.refusedBecause = claim.result.error.explanation;
      return report; // still driven elsewhere — correct outcome, not a failure
    }
    report.claimed = true;
    report.epoch = claim.lease!.epoch;
    this.grantedTtlMs = Math.max(1, Number(claim.lease!.untilMs) - Date.now());

    try {
      // ONE read for the whole takeover: the runs AND every unresolved tool call. Reconciling used
      // to cost a request per conversation; the wide verb makes it a single round trip.
      //
      // RECONCILE BEFORE ADVANCING. A previous driver may have died between dispatching a tool and
      // recording what came back, so some calls' effect on the world is unknown. Advancing first
      // would re-derive from a state nobody has established.
      const view = fromBinary(
        TreeViewReplySchema,
        await this.host.treeView(
          toBinary(TreeViewRequestSchema, create(TreeViewRequestSchema, { rootRunId, wantRuns: true, wantDangling: true })),
        ),
      );
      report.reconciled.push(...view.dangling);

      // DEEPEST FIRST. A parent cannot legitimately conclude before the children it is waiting on
      // have returned — driving in spawn order would have the root reach a conclusion while its own
      // subtree is still unresolved. Depth-descending is the topological order of a tree whose edges
      // all point at the root.
      let pending = view.runs.slice().sort((a, b) => b.depth - a.depth);

      /*
       * THE STEERING CURSOR IS PER RUN AND DURABLE — `AgentRun.steer_seq`, not a local.
       *
       * It used to be one number for the whole tree, reset to zero on every takeover, and that is
       * two failures rather than one. A driver adopting a tree re-read the conversation from the
       * beginning and handed the step every user message ever sent as though it had just arrived —
       * so the user's oldest sentence landed at the END of the prompt, where a model reads it as
       * their most recent. And because runs advance deepest-first while the inbox is read for the
       * whole tree, a correction typed to the ROOT was consumed at a CHILD's step boundary, matched
       * to no run, and delivered to nobody: the user's words reaching no one, which is the single
       * failure this mechanism exists to prevent.
       *
       * Both are the same mistake — a per-run fact kept per-driver. It now lives on the run, seeded
       * at the conversation's head when the run starts and advanced by the progress write that
       * proves the step happened. The property the old comment was protecting still holds: a driver
       * that dies before writing leaves the cursor where it was, so its steer is re-delivered rather
       * than lost.
       */
      let spent = 0;
      /*
       * THE TREE IS RE-READ UNTIL IT STOPS GROWING, because a run can spawn children WHILE it is
       * being driven — which is the entire point of an agent that delegates.
       *
       * A single snapshot taken at takeover cannot contain them. So an agent asked for a sub-agent,
       * the insert succeeded, and this loop drove the PARENT to its step cap waiting for an answer
       * nobody was producing, while the child sat QUEUED and untouched until some later adoption
       * happened to find it. Every individual piece reported success.
       *
       * Bounded by the work itself rather than by a counter: `drive` carries a run to a terminal
       * state, so each pass strictly shrinks the unfinished set, and new runs can only arrive at
       * increasing depth against a hard ceiling. A pass that advances nothing ends it.
       */
      const seen = new Set<string>();
      while (pending.length) {
        let advancedThisPass = 0;
        /*
         * SIBLINGS ARE DRIVEN TOGETHER, a few at a time.
         *
         * `drive` carries one run to a terminal state — every step of it — before returning, and
         * this used to walk `pending` one run after another. So a designer that spawned a model
         * author and two widget authors at once got exactly the opposite of what it asked for: the
         * model author ran alone for twelve minutes while both widget authors sat QUEUED with zero
         * steps, and the designer above them waited on all three. Parallel spawns were serial.
         *
         * Now a small pool pulls from the queue: each run still gets its own drive, its own lease
         * heartbeat and its own credit hold; only the waiting is shared. The cap is what keeps one
         * tree from opening a dozen model calls at once — a fan-out of widget authors is normal, a
         * flood is not. FENCED stops the WHOLE takeover as before: the pool drains and no worker
         * picks up another run once the tree is no longer this driver's.
         */
        const queue = pending.filter((r) => r.status !== RunStatus.FINISHED);
        const driveOne = async (run: AgentRun): Promise<void> => {
          seen.add(run.runId);
          // Scoped to THIS run's own conversation. Both operands of the previous check were always
          // truthy, so every run received every unresolved call in the tree — a run would have been
          // asked to reconcile a sibling's PLC write, which is the reconciliation being wrong in the
          // most dangerous possible direction.
          const dangling = report.reconciled.filter((d) => d.conversationId === run.conversationId);
          const outcome = await this.drive(
            run,
            rootRunId,
            report.epoch,
            step,
            dangling,
            () => spent,
            (c) => (spent += c),
          );
          if (outcome === 'advanced') {
            report.advanced.push(run.runId);
            advancedThisPass++;
          }
          // A run that yielded to its children is NOT finished with, so it is left out of `seen` and
          // picked up again on the pass after they return. It counts as progress: something moved,
          // and the tree it is waiting on is what the next pass will contain.
          if (outcome === 'waiting') {
            seen.delete(run.runId);
            advancedThisPass++;
          }
          // FENCED stops the WHOLE takeover, not just this run. The tree is no longer this driver's:
          // continuing to the next sibling would be spending on a tree someone else now owns, or that
          // the user has stopped. The caller re-invokes, which re-claims and re-reads.
          if (outcome === 'fenced') report.fenced = true;
        };
        /*
         * THE QUEUE IS LIVE, not a snapshot.
         *
         * Workers used to pull from the list taken at the top of the pass and the tree was re-read
         * only after every one of those runs had reached a terminal state or yielded. So anything
         * that APPEARED mid-pass — a child a live run spawned, an agent a note woke — sat QUEUED
         * until the slowest run of the pass was done, however many workers were idle. Observed as
         * a widget author messaging the finished model author and then holding its slot for
         * eleven steps waiting for an answer the wake could not start to give: the two waited on
         * each other until the asker hit its step cap. Every worker was free but one.
         *
         * Now an idle worker with an empty queue, while a sibling is still driving, re-reads the
         * tree and takes whatever is new. Nothing already seen this pass is re-queued (a parent
         * that yielded is the next pass's business, as before), so this cannot spin; and the pass
         * still ends when nobody is driving and nothing is left to pull.
         */
        const queued = new Set(queue.map((r) => r.runId));
        let driving = 0;
        // Idle workers doze until the interval passes OR a sibling finishes — so the pass ends the
        // moment the last run does, rather than up to an interval later.
        let dozing: (() => void)[] = [];
        const doze = (): Promise<void> =>
          new Promise((resolve) => {
            const t = setTimeout(() => {
              dozing = dozing.filter((w) => w !== wake);
              resolve();
            }, REFILL_MS);
            const wake = (): void => {
              clearTimeout(t);
              resolve();
            };
            dozing.push(wake);
          });
        const refill = async (): Promise<void> => {
          const now = fromBinary(
            TreeViewReplySchema,
            await this.host.treeView(toBinary(TreeViewRequestSchema, create(TreeViewRequestSchema, { rootRunId, wantRuns: true }))),
          );
          for (const r of now.runs) {
            if (r.status === RunStatus.FINISHED || seen.has(r.runId) || queued.has(r.runId)) continue;
            queue.push(r);
            queued.add(r.runId);
          }
          queue.sort((a, b) => b.depth - a.depth);
        };
        const worker = async (): Promise<void> => {
          while (!report.fenced) {
            const next = queue.shift();
            if (next) {
              driving += 1;
              try {
                await driveOne(next);
              } finally {
                driving -= 1;
                dozing.splice(0).forEach((wake) => wake());
              }
              continue;
            }
            if (driving === 0) return;
            await doze();
            if (driving > 0) await refill();
          }
        };
        await Promise.all(Array.from({ length: Math.min(CONCURRENT_RUNS, queue.length) }, worker));
        if (report.fenced || !advancedThisPass) break;

        // Anything that appeared while we were working — a delegated child, most of all.
        const again = fromBinary(
          TreeViewReplySchema,
          await this.host.treeView(toBinary(TreeViewRequestSchema, create(TreeViewRequestSchema, { rootRunId, wantRuns: true }))),
        );
        pending = again.runs.filter((r) => r.status !== RunStatus.FINISHED && !seen.has(r.runId)).sort((a, b) => b.depth - a.depth);
      }
    } finally {
      // Always hand the tree back. A worker that finishes and holds its lease makes the tree
      // un-adoptable for a full TTL for no reason, and a user re-opening a tab would be told it is
      // being driven elsewhere when it is not.
      await this.host.lease(
        toBinary(
          LeaseRequestSchema,
          create(LeaseRequestSchema, {
            action: LeaseRequest_Action.RELEASE,
            rootRunId,
            driverId: this.opts.driverId,
            epoch: report.epoch,
          }),
        ),
      );
    }

    return report;
  }

  /** Advance one run to a terminal state, renewing the lease as it goes. */
  private async drive(
    run: AgentRun,
    rootRunId: string,
    epoch: bigint,
    step: StepFn,
    dangling: DanglingToolCall[],
    spent: () => number,
    charge: (c: number) => void,
  ): Promise<DriveOutcome> {
    const cap = run.task?.constraints?.stepCap || this.opts.maxStepsPerRun;
    /*
     * A RUN THAT PARKED ITSELF IS NOT STEPPED — not even once.
     *
     * The store reads a run as AWAITING for three reasons: an open child (`awaits`), an open question
     * to the person (`ask_user`), and an open wait for later. Only the first leaves something here to
     * drive; a question and a wait are both answered from OUTSIDE the driver (a person's card, a
     * wake), so neither counts as dangling — see `danglingToolCalls`, which excludes both. What is
     * left — awaiting, no child, nothing dangling — is a park: stepping it would reserve credit, call
     * the model, and hand the provider a tool call with no result yet, which most providers reject
     * outright. It returns before the lease is even renewed: whatever resolves the question or the
     * wait schedules a fresh takeover, and that one finds a run that is no longer waiting.
     */
    if (run.status === RunStatus.AWAITING && run.awaits.length === 0 && dangling.length === 0) return 'parked';
    let handed = false;
    // The run's OWN ceiling, checked against its DURABLE accumulated spend. A per-invocation tally
    // was the whole reason re-adoption re-spent the budget: each takeover started the count at zero,
    // so a tree adopted three times cost three budgets. Nothing here counts anything — the store
    // keeps the totals and echoes them back on every progress write, which is what makes "stateless
    // between takeovers" true rather than merely claimed.
    const runBudget = run.task?.constraints?.budgetUsd || 0;
    let cur = run;

    // Consecutive steps that returned nothing usable. Per-takeover rather than durable on the run:
    // a fresh driver deserves its own attempt, and the condition is a property of the model call
    // rather than of the run's history.
    let unproductive = 0;
    /*
     * Consecutive refusals per TOOL, cleared the moment that tool answers.
     *
     * Per-takeover like `unproductive` beside it, and for the same reason: this describes what the
     * tools are doing for THIS driver, and a fresh one deserves its own attempt at a tool that may
     * well have been failing for a reason that has since gone away.
     */
    const toolFailures = new Map<string, number>();
    /** The refusal each of those tools last gave, so a stop can say WHY and not just how often.
     *  Lives and dies with the count beside it — see where both are cleared. */
    const toolRefusals = new Map<string, string>();
    for (;;) {
      if (cur.stepCount >= cap) break; // durable count, so it survives this driver being replaced
      // THE AUTHORIZATION CHECKPOINT, and there is exactly one, immediately before the paid call.
      //
      // The heartbeat IS the liveness signal — a driver that stops renewing has, by definition,
      // stopped driving. What was missing is that renewal answers in BOTH directions: a refusal here
      // means the tree is no longer this driver's to spend on, because the user stopped everything
      // or another driver took over. Ignoring that answer is how "stop" stopped the bookkeeping and
      // not the spending: the records read ABORTED while the model kept being called to the cap.
      //
      // Checking it here rather than after the step is the whole point — an authorization check that
      // runs after the money is spent is an audit log, not a control.
      /*
       * THE RENEWAL AND THE INBOX GO TOGETHER, because neither is waiting on the other.
       *
       * Every step used to open with three round trips in a row — renew the lease, read the steer
       * inbox, reserve credit — each awaited before the next was sent, and only then the model call.
       * Measured on the local stack the calls themselves are fast (a step's inference averages
       * 736ms, its tools 200-500ms), so this serial preamble is a real share of a step's wall clock,
       * and every one of those trips crosses the same wire the model call does — over the internet
       * it is the larger share.
       *
       * They are independent facts: "is this tree still mine" and "has anyone said anything to this
       * run". Issued together, they cost ONE round trip instead of two, and nothing about either
       * answer changes by having been asked at the same time.
       *
       * The renewal is still CHECKED FIRST and still ends the run before anything is spent — that
       * is the authorization checkpoint, and it keeps its position in the order the code reads. What
       * changed is when the second request left, not when its answer is used.
       */
      const [renewBytes, inboxBytes] = await Promise.all([
        this.host.lease(
          toBinary(
            LeaseRequestSchema,
            create(LeaseRequestSchema, {
              action: LeaseRequest_Action.RENEW,
              rootRunId: run.rootRunId,
              driverId: this.opts.driverId,
              epoch,
              ttlMs: this.opts.leaseTtlMs,
            }),
          ),
        ),
        this.host.treeView(
          toBinary(
            TreeViewRequestSchema,
            create(TreeViewRequestSchema, {
              rootRunId,
              wantInjections: true,
              injectionsSinceSeq: cur.steerSeq,
            }),
          ),
        ),
      ]);
      const renew = fromBinary(LeaseReplySchema, renewBytes);
      if (renew.result?.error) return 'fenced';

      // Two ceilings, checked BEFORE the step so exhaustion ends a run at a boundary with a named
      // reason rather than mid-call with a truncated result the parent cannot interpret.
      //
      // They answer different questions and neither pretends to be the other: the RUN's budget is
      // the user's, and is durable because the job outlives any one driver. The TAKEOVER's budget is
      // this worker invocation's blast radius across every run it touches, and is correctly
      // per-invocation for exactly that reason.
      const runSpend = cur.usage?.costUsd ?? 0;
      if (runBudget > 0 && runSpend >= runBudget) {
        await this.finish(
          run,
          epoch,
          TerminalReason.BUDGET,
          `Stopped: this run's budget of $${runBudget.toFixed(2)} is spent after ${cur.stepCount} steps.`,
        );
        return 'advanced';
      }
      if (this.opts.budgetUsd > 0 && spent() >= this.opts.budgetUsd) {
        await this.finish(run, epoch, TerminalReason.BUDGET, `Stopped: this worker's budget is spent after ${cur.stepCount} steps.`);
        return 'advanced';
      }

      // STEERING PICKUP, at the step boundary, from THIS RUN's own durable cursor — read above,
      // alongside the renewal, and decoded here where it is used.
      const inbox = fromBinary(TreeViewReplySchema, inboxBytes);
      // Only what was said to THIS run advances THIS run's cursor. A sibling's correction is left
      // exactly where it is, for the step boundary of the run it was actually addressed to.
      const steers = inbox.injections.filter((m) => m.runId === run.runId);
      const steerSeq = steers.reduce((hi, m) => (m.seq > hi ? m.seq : hi), cur.steerSeq);

      // RESERVE BEFORE SPENDING. A refusal ends the run with its own reason rather than looking like
      // an exhausted budget — the run may be well inside its ceiling and simply unaffordable.
      //
      // The estimate is this run's average step so far — but FLOORED, and the floor is the
      // load-bearing part rather than a rounding nicety.
      //
      // That average is computed from usage the client reported, so a client reporting zero also
      // drives its own estimate to zero. An adversarial test caught exactly that: with an unfloored
      // estimate, a lying client requested holds of $0.00, which any ledger grants forever, and ran
      // to its step cap on a ten-cent balance. Closing the settle side alone was not enough — the
      // same lie re-entered through the reserve side.
      //
      // A hold of zero is not a hold. The floor means every step costs the account something to
      // attempt, whatever the client claims, so an under-reporting client runs out of money instead
      // of running free.
      let held: CreditReservation | null = null;
      if (this.opts.credits) {
        const spentSoFar = cur.usage?.costUsd ?? 0;
        const avg = cur.stepCount > 0 ? spentSoFar / cur.stepCount : 0;
        const estimate = Math.max(avg, MIN_HOLD_USD);
        held = await this.opts.credits.reserve(run.runId, estimate);
        if (!held) {
          await this.finish(run, epoch, TerminalReason.NO_CREDITS, 'Stopped: this account has no credits left for another step.');
          return 'advanced';
        }
      }

      // THE HEARTBEAT RUNS DURING THE CALL, not around it.
      //
      // Renewing only between steps made the TTL an implicit claim about how long one model call
      // takes — and a call with extended reasoning routinely outlives any TTL short enough for
      // reclaim to feel fast. The lease then lapsed mid-step, a sweeper adopted the tree, and the
      // original driver came back with a finished step and a fenced write: tokens spent, work
      // discarded, and the adopter paying for the same step again.
      //
      // "How long a unit of work takes" and "how long before we declare you dead" are separate
      // facts, so they get separate mechanisms. The TTL stays short; this keeps it fresh.
      const beat = this.heartbeat(run.rootRunId, epoch);
      let res: StepResult;
      try {
        // `cur`, not `run`. The step gets the record as it IS — the snapshot taken at takeover has
        // a step count and a spend that stopped being true after the first iteration, and an agent
        // reasoning about how much road it has left needs the live numbers, not the ones from when
        // this driver happened to pick the tree up.
        /*
         * THE LAST STEP IS ANNOUNCED AS THE LAST STEP.
         *
         * `cur.stepCount` is the durable count of steps ALREADY taken, so the step about to run is
         * number `stepCount + 1`, and it is the final one when that reaches the cap. Telling the
         * agent turns a truncation into a conclusion: it spends the step it already had on saying
         * what it found, rather than on a tool call whose result nobody will ever be called back to
         * read. Nothing extra is spent — this is the same step, differently briefed.
         */
        // Handed over ONCE: the step records each open call's outcome as unknown (see step.ts), so a
        // later step of this same drive must not be told about it again as though it were still open.
        res = await step(cur, { dangling: handed ? [] : dangling, steers, epoch, lastCall: cur.stepCount + 1 >= cap });
        handed = true;
      } catch (e) {
        /*
         * A THROWN STEP IS A TERMINAL RUN, NOT A TERMINAL DRIVER.
         *
         * There was no catch here at all, so a provider 500, a dropped socket or a parse blowing up
         * escaped the whole takeover. The run stayed RUNNING forever with a stale headline, which
         * reads as "still working"; its parent stayed AWAITING, so the branch above it hung; and
         * with an adoption alarm in place the tree is picked up again a minute later and the same
         * step throws again — an invisible retry loop that can spend on every lap.
         *
         * So it ends, and it says what happened in words a person can read. The work is not lost:
         * the conversation, the objective and the ceilings are all still on the record, and
         * continuing the run resumes them as an attempt that is TOLD the last one failed. Recovery
         * is one deliberate "try again" instead of a spinner that never resolves.
         *
         * Deliberately not a retry-in-place. A failure that is going to repeat should cost one
         * attempt and say so, and one that was transient costs the person a single click — the
         * asymmetry runs the safe way, because a silent retry loop is the expensive mistake.
         */
        beat.stop();
        if (held && this.opts.credits) await this.opts.credits.settle(held);
        const why = e instanceof Error ? e.message : String(e);
        await this.finish(run, epoch, TerminalReason.ERROR, `Stopped: the step failed — ${why}`);
        return 'advanced';
      } finally {
        beat.stop();
        // SETTLED IN `finally` so a thrown step releases its hold instead of stranding it — and with
        // NO AMOUNT. The loop does not get to say what a step cost, because what it knows came from
        // the provider through the browser, and a client that reports zero would otherwise run to
        // its step cap for free. The ledger charges from what IT observed.
        if (held && this.opts.credits) await this.opts.credits.settle(held);
      }
      if (beat.lost) return 'fenced'; // the tree stopped being ours WHILE we were working
      charge(res.usage?.costUsd ?? 0);

      // The second net, and it catches a different case than the renewal above: a run cancelled by
      // its own PARENT (a consumer decision the driver itself made) does not fence the lease, so the
      // driver can still hold a stale `pending` entry for it. A refused progress write is that run
      // saying it is already terminal. Bounded to one call rather than zero, which is the honest
      // trade for not fencing a driver on its own decision.
      const wrote = fromBinary(
        RunMutationReplySchema,
        await this.host.applyRun(
          toBinary(
            RunMutationSchema,
            create(RunMutationSchema, {
              runId: run.runId,
              epoch,
              op: {
                case: 'progress',
                value: create(RunProgressSchema, {
                  headline: res.headline,
                  /*
                   * AND THE STREAMING TEXT IS CLEARED HERE.
                   *
                   * The step has landed, so its durable message exists and the viewer should be
                   * reading THAT. Leaving the partial up would double the answer on screen — once
                   * as the record, once as the ghost of it being written — and a run that finished
                   * would keep a half-sentence next to its conclusion forever.
                   */
                  partial: '',
                  usage: res.usage ?? create(RunUsageSchema, {}),
                  status: RunStatus.RUNNING,
                  // Consumed only now, because this write is what proves the step happened.
                  steerSeq,
                }),
              },
            }),
          ),
        ),
      );
      if (wrote.result?.error) return 'stale';
      // The echo IS the driver's state. A write that succeeded and returned nothing means the run is
      // no longer there to advance, and looping on a stale copy would spend against a record that
      // has moved on without us.
      if (!wrote.run) return 'stale';
      cur = wrote.run;

      /*
       * IT DELEGATED — SO IT YIELDS.
       *
       * AWAITING is derived by the store: running, and some child has not come back. A parent that
       * keeps stepping through that is asking for an answer nobody has produced yet, and it does so
       * until its cap runs out — which is exactly what happened to the first agent that used the
       * spawn tool. It burned six steps waiting while its child sat QUEUED, because the driver was
       * still inside this loop and had not gone back to look at the tree.
       *
       * Returning here hands control to the takeover loop, which drives the children and comes back.
       * Bounded by the parent's own step cap: each wait follows a step it has already paid for.
       */
      if (cur.status === RunStatus.AWAITING) return 'waiting';

      if (res.done) {
        /*
         * A REFUSED FINISH IS NOT A FINISH.
         *
         * The store refuses a COMPLETED return while agents this run started have not answered —
         * the one case where a model states a result it cannot have. Yielding rather than looping
         * hands control back to the driver, which goes and advances those children; their returns
         * are delivered into this run's own conversation, and it steps again holding the real
         * answers instead of the invented one.
         *
         * Reported as 'waiting' because that is exactly what it is, and it is the state the
         * takeover loop already knows how to make progress on.
         */
        const took = await this.finish(run, epoch, res.done.terminalReason, res.done.summary, res.done.deliverableJson, res.done.unsourced, res.done.misattributed, res.done.uncited, res.done.citations);
        return took ? 'advanced' : 'waiting';
      }

      // A run that produces nothing, twice, will produce nothing forever.
      //
      // The inputs are the same each time — same system prompt, same task, same history (an empty
      // reply is not appended, because a message with no content is not a message) — so the loop
      // would be resending an identical request and expecting a different answer, paying for every
      // attempt until the cap. Two rather than one: a single empty reply can be sampling, and one
      // retry is cheap. A second is structural.
      unproductive = res.unproductive ? unproductive + 1 : 0;
      if (unproductive >= UNPRODUCTIVE_LIMIT) {
        await this.finish(
          run,
          epoch,
          TerminalReason.NO_OUTPUT,
          `Stopped: the model returned no output ${unproductive} times in a row. This is usually a token budget consumed entirely by reasoning — try a larger limit or a non-reasoning model.`,
        );
        return 'advanced';
      }

      /*
       * A TOOL THAT KEEPS REFUSING IS NOT PROGRESS, however busy the run looks.
       *
       * A tool turn reports "using <tool>" whether the tool answered or refused, so a run reaching
       * for one broken tool every turn was indistinguishable from a run doing work — and it was
       * billed for each attempt until the cap. Observed: a specialist calling a tool no expert
       * declares, refused every time, rewriting its arguments on each retry.
       *
       * The rewriting is why the count is keyed on the NAME alone. A model that cannot get a tool
       * to work changes its arguments on every attempt, so a key including them is new each time
       * and would never reach any limit.
       *
       * Success CLEARS it, which is what keeps this from ending honest runs: a tool that refuses
       * twice and then answers was being told something useful, and the model acted on it.
       */
      for (const name of res.toolsWorked ?? []) {
        toolFailures.delete(name);
        toolRefusals.delete(name);
      }
      for (const name of res.toolsFailed ?? []) toolFailures.set(name, (toolFailures.get(name) ?? 0) + 1);
      // Kept beside the count and cleared with it, so the sentence a stop carries is the refusal
      // this tool is stuck on — never one left behind by a tool that has since started answering.
      for (const [name, message] of Object.entries(res.toolFailureMessages ?? {})) toolRefusals.set(name, message);
      const stuck = [...toolFailures].find(([, n]) => n >= TOOL_FAILURE_LIMIT);
      const lastRefusal = stuck ? toolRefusals.get(stuck[0]) : undefined;
      if (stuck) {
        await this.finish(
          run,
          epoch,
          TerminalReason.TOOL_FAILING,
          /*
           * THE REFUSAL TRAVELS WITH THE STOP. It used to say the reason was "in the transcript
           * above", which is true for a person reading the child's thread and false for the parent,
           * which receives only this sentence. A parent that cannot see WHY reaches for the most
           * plausible cause instead — observed: it blamed missing RBAC write permission and told
           * the person to edit roles, when the tool had refused over a broken validator regex.
           */
          `Stopped: "${stuck[0]}" failed ${stuck[1]} times in a row. The run was working, but the tool it kept reaching for would not answer.` +
            (lastRefusal ? ` Its last refusal was: ${lastRefusal}` : ''),
        );
        return 'advanced';
      }
    }

    // Ran out of road rather than concluding — and it says so, because a parent that cannot tell
    // those apart will act on a truncated answer as though it were a finding.
    await this.finish(run, epoch, TerminalReason.STEP_CAP, `Stopped after ${cap} steps without concluding.`);
    return 'advanced';
  }

  /** Keep the lease fresh across one long await. Stops on the first refusal and reports it, because
   *  a refusal means the tree is no longer ours and continuing to beat would be pretending. */
  private heartbeat(rootRunId: string, epoch: bigint): { stop: () => void; lost: boolean } {
    // The period comes from the window the store GRANTED, not from the one we asked for. Those can
    // differ — the store clamps — and beating against our own request would mean beating slower than
    // the grant we actually hold, which is the one thing this must never do.
    const ttl = this.grantedTtlMs;
    const state = { stop: () => {}, lost: false };
    // A third of the window, with NO lower floor. A floor is tempting as runaway protection, and a
    // floor that can exceed ttl/3 silently defeats the very invariant it sits inside — the beat
    // lands after the grant it was meant to refresh has already lapsed. The window is the only
    // number that may set this period.
    const timer = setInterval(
      () => {
        void (async () => {
          const reply = fromBinary(
            LeaseReplySchema,
            await this.host.lease(
              toBinary(
                LeaseRequestSchema,
                create(LeaseRequestSchema, {
                  action: LeaseRequest_Action.RENEW,
                  rootRunId,
                  driverId: this.opts.driverId,
                  epoch,
                  ttlMs: this.opts.leaseTtlMs,
                }),
              ),
            ),
          );
          if (reply.result?.error) {
            state.lost = true;
            clearInterval(timer);
          }
        })();
      },
      Math.max(1, Math.floor(ttl / 3)),
    );
    state.stop = () => clearInterval(timer);
    return state;
  }

  /**
   * End the run — and REPORT WHETHER IT TOOK.
   *
   * It used to fire and drop the reply, which was fine while every refusal was a lost lease. The
   * store now also refuses a COMPLETED return from a run whose delegated agents have not answered
   * (the fabrication guard, `finishRun`), and swallowing that one would be the worst possible
   * handling: the loop would report a completed run to its caller while the store still had it
   * RUNNING, so the driver and the record would disagree about whether the work was done.
   *
   * Returns true when the run is now terminal.
   */
  private async finish(
    run: AgentRun,
    epoch: bigint,
    terminalReason: TerminalReason,
    summary: string,
    deliverableJson = '',
    /** Figures this answer asserts that appear nowhere in what the run received. Carried onto the
     *  return so the finding travels with the answer it is about — a caveat delivered by any other
     *  route is one nobody sees. */
    unsourced: string[] = [],
    /** Claims that contradict the record. Carried beside `unsourced` rather than merged, because a
     *  reader reacts differently to "never measured" than to "belongs to something else". */
    misattributed: string[] = [],
    uncited: string[] = [],
    citations: string[] = [],
  ): Promise<boolean> {
    const reply = fromBinary(
      RunMutationReplySchema,
      await this.host.applyRun(
        toBinary(
          RunMutationSchema,
          create(RunMutationSchema, {
            runId: run.runId,
            epoch,
            op: { case: 'finish', value: create(AgentReturnSchema, { terminalReason, deliverableJson, unsourced, misattributed, uncited, citations, result: { summary } }) },
          }),
        ),
      ),
    );
    const took = !reply.result?.error;
    /*
     * SAID AT THE WRITE, not at the end of the takeover.
     *
     * Announcing after the whole tree settles would put the ending behind however many siblings are
     * still being driven — which on a fan-out of six is the thing a reader is waiting for arriving
     * last. Only on a write that actually landed: a refused finish is not an ending, and telling a
     * watcher about one would make a run that is still going look finished.
     *
     * Guarded, because a watcher is somebody else's code. A repaint that throws must not take down
     * the loop that was only being polite enough to notify it.
     */
    if (took) {
      try {
        this.opts.onRunTerminal(run.runId);
      } catch {
        /* a watcher's failure is not the run's */
      }
    }
    return took;
  }
}
